import { NavLink } from "react-router-dom";
import { HeartPulse, LayoutDashboard, CalendarDays, Users, ClipboardList, LogOut, FolderOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/employees", label: "Employees", icon: Users },
  { to: "/patients", label: "Patients & Visits", icon: ClipboardList },
  { to: "/documents", label: "Documents", icon: FolderOpen },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="no-print fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-white/40 bg-white/70 backdrop-blur-2xl md:flex" data-testid="sidebar">
        <div className="flex items-center gap-2.5 border-b border-slate-200 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#2C5545] text-white">
            <HeartPulse className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading text-sm font-semibold leading-tight text-slate-900">Hospice Care</p>
            <p className="text-xs text-slate-500">Staff Scheduler</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-[#2C5545]/10 text-[#1E3B30]" : "text-slate-500 hover:bg-[#2C5545]/5 hover:text-[#1E3B30]"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/40 p-4">
          <p className="mb-2 truncate text-xs text-slate-500" data-testid="user-email">{user?.email}</p>
          <button
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-[#2C5545]/30 px-3 py-1.5 text-xs font-bold text-[#2C5545] transition-colors duration-200 hover:bg-[#2C5545] hover:text-white"
            data-testid="logout-btn"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <header className="no-print sticky top-0 z-20 flex items-center gap-2 border-b border-white/40 bg-white/70 px-4 py-3 backdrop-blur-2xl md:hidden">
        <HeartPulse className="h-5 w-5 text-slate-900" />
        <span className="font-heading text-sm font-semibold">Hospice Scheduler</span>
        <nav className="ml-auto flex gap-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              aria-label={label}
              data-testid={`mobile-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-md p-2 ${isActive ? "bg-[#2C5545]/10 text-[#1E3B30]" : "text-slate-500"}`
              }
            >
              <Icon className="h-4 w-4" />
            </NavLink>
          ))}
        </nav>
        <button onClick={logout} className="rounded-md p-2 text-slate-500 transition-colors hover:text-[#1E3B30]" data-testid="mobile-logout-btn">
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      <main className="md:pl-60">
        <div className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
